import { SETTING_TAB_WIDGET_KEYS } from "../../../other/constants/OtherKeys.jsx";
import SettingsTabBase from "./SettingsTabBase.jsx";
import { getSwivuAssitText, updateSwivuAssistText } from "../../../network/network.jsx";
import { showToast, transformSwivuGridRecords } from "../../../other/utils/utils.jsx";
import { userPreferences } from "../../../react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { SERVER_BASE_URL } from "../../../env.jsx";
import { triggerSwivuAssistUpdate } from "../../../modern/components/editors/swivuAssist/SwivuAssistEditor";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";

const ASSIST_STEPS = [
  {
    number: 1,
    title: "Start with the Template",
    content: [
      "From the File menu, select Make a Copy to create your own editable version."
    ],
    hasLink: true,
    linkText: "Click here to open the Swivu Assist template on Google Sheets",
    linkCls: "!text-blue-500 hover:underline cursor-pointer"
  },
  {
    number: 2,
    title: "Edit Your Copy",
    content: [
      "Update the content to match your needs.",
      "IMPORTANT: Do not change the ID column. This column maps each row to the correct Swivu Assist surface."
    ],
    tip: "Tip: To find the ID of a Swivu Assist surface, hover over the \"i\" icon in that surface's header."
  },
  {
    number: 3,
    title: "Share Your Sheet",
    content: [
      "In Google Sheets, go to Share settings and set access to \"Anyone with the link\" as Viewer.",
      "Copy the share link."
    ]
  },
  {
    number: 4,
    title: "Paste and Update",
    content: [
      "Paste the share link into the field below, then click Update."
    ],
    hasInput: true
  },
  {
    number: 5,
    title: "Need to Go Back?",
    content: [
      "To revert to Swivu's default help content, simply delete your link from the box and click Update."
    ]
  }
];


export default class SettingsSwivuAssistUpdateTab extends SettingsTabBase {
  static get $name() {
    return "Settingsswivuassistupdatetab";
  }

  static get type() {
    return "settingsswivuassistupdatetab";
  }

  get swivuDeepLinkSlug() {
    return SETTING_TAB_WIDGET_KEYS.swivuAssistUpdateTab;
  }

  static get configurable() {
    return {
      items: {
        containerLayout: {
          type: "container",
          cls: "w-full h-full overflow-y-auto px-4 py-2",
          items: {
            titleContainer: {
              type: "container",
              cls: "mb-2",
              html: "<h2 class='text-lg font-semibold my-0'>Swivu Assist</h2>" +
                "<p class='my-[0.1em]'>Swivu Assist provides surface-specific online help inside Swivu. You can create your own custom help content using Google Sheets by following these steps:</p>"
            },
            stepsContainer: {
              type: "container",
              cls: "flex-col space-y-2 !my-0 !py-0 w-full",
              items: ASSIST_STEPS.map((step) => {
                const stepItems = {
                  type: "container",
                  cls: "flex-col",
                  items: {
                    titleRow: {
                      type: "container",
                      cls: "flex items-start gap-0",
                      html: `<div class="font-semibold">${step.number}. ${step.title}</div>`
                    },
                    contentRows: {
                      type: "container",
                      cls: "flex-col ml-4 gap-[0.1em]",
                      items: step.content.map(text => ({
                        type: "container",
                        html: `<p class="my-[0.1em]">${text.includes("IMPORTANT") ? text.replace("IMPORTANT", "<span class='text-red-600 font-medium'>IMPORTANT</span>") : text}</p>`
                      }))
                    }
                  }
                };

                // Add link if needed
                if (step.hasLink) {
                  stepItems.items.contentRows.items.unshift({
                    type: "container",
                    cls: "mt-1",
                    html: `<a class="${step.linkCls}" href='${SERVER_BASE_URL}/swivuassist/template-url' target="_blank">${step.linkText}</a>`
                  });
                }

                // Add tip if needed
                if (step.tip) {
                  stepItems.items.tipRow = {
                    type: "container",
                    cls: "ml-4 mt-1 text-gray-500 italic",
                    html: step.tip
                  };
                }

                // Add input field if needed
                if (step.hasInput) {
                  stepItems.items.inputRow = {
                    type: "container",
                    cls: "ml-4 mt-2",
                    items: {
                      googleSheetLinkField: {
                        type: WIDGET_KEYS.fieldUrl,
                        name: "googleSheetLinkField",
                        cls: "w-full",
                        placeholder: "Paste a link to a publicly accessible Google Sheet here",
                        onInput: "up.onSwivuAssistLinkUpdate"
                      }
                    }
                  };
                }
                return stepItems;
              })
            },
            reminderContainer: {
              type: "container",
              cls: "mt-[0.8em] text-gray-500 !w-full",
              html: "Reminder: Every time you make changes to your Google Sheet, click Update to sync."
            },
            buttonContainer: {
              type: "container",
              cls: "flex justify-end mt-4 !w-full",
              items: {
                updateGloablAssistButton: {
                  type: "button",
                  text: "Update Global",
                  cls: "b-raised b-blue !min-w-[6.3em] disabled:!cursor-not-allowed",
                  onClick: "up.onUpdateSwivuAssistTextForGlobal",
                  disabled: false
                },
                updateBoardAssistButton: {
                  type: "button",
                  text: "Update",
                  cls: "b-raised b-blue !min-w-[6.3em] disabled:!cursor-not-allowed",
                  onClick: "up.onUpdateSwivuAssistTextForBoard",
                  disabled: false
                }
              }
            }
          },
        },
      },
    };
  }


  editRecord(record, context) {
    super.editRecord(record, context, { isFirstShow: true });
    this.context = context;
    this.record = record;

    const { googleSheetLinkField } = this.widgetMap;
    googleSheetLinkField.value = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, '', true)
    this.setButtonsState();
  }

  onUpdateSwivuAssistTextForGlobal() {
    const isSuperUser = this.context.user?.is_superuser;
    if (isSuperUser) {
      this.updateSwivuAssist();
    }
  }

  /**
   * Validates if the provided URL is a valid Google Sheets URL
   * @param {string} url - The URL to validate
   * @returns {boolean} - True if valid, false otherwise
   */
  isValidGoogleSheetsUrl(url) {
    // Allow empty string (for reverting to default)
    if (url === '') {
      return true;
    }

    url = url.trim();

    // Google Sheets URL patterns
    const googleSheetsPatterns = [
      // Standard sharing URL
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/edit(\?[^#]*)?(\\#.*)?$/,
      // Export URL
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/export\?format=csv(&.*)?$/,
      // Alternative sharing URL format
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+\/.*$/
    ];

    return googleSheetsPatterns.some(pattern => pattern.test(url));
  }

  onUpdateSwivuAssistTextForBoard() {
    const { googleSheetLinkField } = this.widgetMap;
    const selectedBoardRecord = this.context.selectedBoard.record;
    const isBoardOwner = selectedBoardRecord.isOwner;
    const boardAssistFieldValue = googleSheetLinkField.value;

    if (isBoardOwner) {
      const selectedBoardId = selectedBoardRecord.id;
      const currentValue = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, '', true);
      const record = this.project.boardPreferencesStore.find(r => r.key === PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl);
      // If the value hasn't changed, just update the content without changing the setting
      if (currentValue !== boardAssistFieldValue) {
        this.project.on({
          sync(e) {
            if ((e.response.boardpreferences?.rows && e.response.boardpreferences.rows.find((up) => up.key ===  PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl)) || (e.response.boardpreferences.removed && record && record.key === PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl)) {
              this.updateSwivuAssist(selectedBoardId);
            }
          },
          once: true,
          thisObj: this,
        })

        if (boardAssistFieldValue === '' && record) {
          this.project.boardPreferencesStore.remove(record.id);
        } else {
          // Update the setting which will trigger the boardPreferencesStore change event
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl, String(boardAssistFieldValue), true);
        }
      } else {
        this.updateSwivuAssist(selectedBoardId);
      }
    }
  }

  updateSwivuAssist(selectedBoardId) {
    this.mask('Updating Swivu Assist Text...');
    updateSwivuAssistText(selectedBoardId).then((resp) => {
      showToast({ msg: resp.data.message, type: "success" });
    }).catch((err) => {
      showToast({ msg: err.response?.data?.detail || "Something went wrong. Unable to update Swivu Assist text", type: "error" });
    }).finally(() => {
      this.refreshSwivuAssistContent(this.context.selectedBoard.id);
      this.unmask();
    });
  }

  setButtonsState() {
    const { updateGloablAssistButton, updateBoardAssistButton, googleSheetLinkField } = this.widgetMap;

    const isSuperUser = this.context.user?.is_superuser;
    const isBoardOwner = this.context.selectedBoard.record.isOwner;

    updateGloablAssistButton.disabled = !isSuperUser;
    updateBoardAssistButton.disabled = !(this.isValidGoogleSheetsUrl(googleSheetLinkField.value) && (isSuperUser || isBoardOwner));

    googleSheetLinkField.disabled = !(isSuperUser || isBoardOwner);
  }

  onSwivuAssistLinkUpdate(e) {
    const { updateBoardAssistButton } = this.widgetMap;
    updateBoardAssistButton.disabled = !this.isValidGoogleSheetsUrl(String(e.value))
  }

  refreshSwivuAssistContent(boardId) {
    getSwivuAssitText(boardId).then((resp) => {
      transformSwivuGridRecords(resp.data);
      // Trigger global update to refresh all Swivu Assist components
      triggerSwivuAssistUpdate();
    })
  }
}

SettingsSwivuAssistUpdateTab.initClass();
